import React, { useContext, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLink, faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import AuthContext from '../../middlewares/AuthContext'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

const EXAMPLE_KEYS = [
  'XXXXXYYYYYZZZZZXXXXXYYYYY',
  'AAAAABBBBBCCCCCDDDDDEEEEE',
  'FFFFFGGGGGHHHHHJJJJJKKKKK',
]

const formSchema = Yup.object().shape({
  licenses: Yup.string().trim().min(25).max(2600).required(),
})

const LinkLicenses: React.FC = () => {
  const { getHeaders }       = useContext(AuthContext) as AuthCtx
  const { t }                = useTranslation()
  const navigate             = useNavigate()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (
    values    : { licenses: string },
    { resetForm }: { resetForm: () => void }
  ) => {
    const licenses = values.licenses
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l.length > 20 && l.length < 30)

    if (!licenses.length) {
      Swal.fire({ icon: 'warning', title: 'No valid license keys found' })
      return
    }

    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post<{ response: string }>(
        `${url}licenses/link`,
        { licenses },
        { headers }
      )
      resetForm()
      const result = await Swal.fire({
        icon             : 'success',
        title            : res.data.response,
        showCloseButton  : false,
        allowOutsideClick: false,
        allowEscapeKey   : false,
      })
      if (result.isConfirmed) navigate('/licenses')
    } catch (err: any) {
      Swal.fire({
        icon  : 'error',
        title : err?.response?.data?.response,
        text  : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="row justify-content-center">
      <div className="col-12">

        {/* ── Header card ───────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          marginBottom    : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width           : 40,
              height          : 40,
              borderRadius    : 10,
              backgroundColor : `${BRAND}18`,
              display         : 'flex',
              alignItems      : 'center',
              justifyContent  : 'center',
              flexShrink      : 0,
            }}>
              <FontAwesomeIcon icon={faLink} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
              {t('licenses.link.link_licenses')}
            </p>
          </div>
        </div>

        {/* ── Form card ─────────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>
            <Formik
              initialValues={{ licenses: '' }}
              validationSchema={formSchema}
              onSubmit={handleSubmit}
            >
              <Form>
                <div className="row g-4">

                  {/* ── Textarea ──────────────────────────────────────── */}
                  <div className="col-md-9">
                    <label
                      htmlFor="licenses"
                      style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                    >
                      {t('licenses.link.your_licenses')}
                    </label>
                    <Field
                      as="textarea"
                      id="licenses"
                      name="licenses"
                      className="form-control"
                      placeholder={t('licenses.link.your_licenses')}
                      style={{
                        height      : 280,
                        resize      : 'vertical',
                        fontFamily  : 'monospace',
                        fontSize    : '0.875rem',
                        lineHeight  : 1.8,
                        borderColor : '#e2e8f0',
                        borderRadius: 10,
                      }}
                    />
                    <ErrorMessage
                      name="licenses"
                      component="p"
                      className="text-danger small fw-bold mt-1 mb-0"
                    />
                  </div>

                  {/* ── Example panel ─────────────────────────────────── */}
                  <div className="col-md-3">
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('licenses.link.example')}
                    </p>
                    <div style={{
                      backgroundColor : '#f8fafc',
                      border          : '1px solid #e2e8f0',
                      borderRadius    : 10,
                      padding         : '0.85rem 1rem',
                    }}>
                      {EXAMPLE_KEYS.map((key, i) => (
                        <p key={i} style={{ margin: '0 0 0.35rem', fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b', letterSpacing: '0.03em' }}>
                          {key}
                        </p>
                      ))}
                      <p style={{ margin: '0.65rem 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
                        25 chars · one per line
                      </p>
                    </div>
                  </div>

                </div>

                {/* ── Submit ────────────────────────────────────────────── */}
                <div className="mt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      display         : 'inline-flex',
                      alignItems      : 'center',
                      gap             : '0.5rem',
                      padding         : '0.55rem 2rem',
                      borderRadius    : 9999,
                      backgroundColor : loading ? '#94a3b8' : BRAND,
                      color           : '#fff',
                      fontWeight      : 700,
                      fontSize        : '0.875rem',
                      border          : 'none',
                      cursor          : loading ? 'not-allowed' : 'pointer',
                      boxShadow       : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                      transition      : 'filter 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                  >
                    {loading
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : <FontAwesomeIcon icon={faPaperPlane} />
                    }
                    <span>{t('licenses.link.send_licenses')}</span>
                  </button>
                </div>

              </Form>
            </Formik>
          </div>
        </div>

      </div>
    </div>
  )
}

export default LinkLicenses
